import { useState } from "react"
import { useParams } from "react-router-dom"
import { useQuery } from "@tanstack/react-query"
import { apiTenant } from "../lib/api"

// ────────────────────────────────────────────────────────────
// T-10 (annual APIT certificate)
// ────────────────────────────────────────────────────────────

interface T10Row {
  employeeId: string
  employeeNo: string
  fullName: string
  nic: string | null
  epfNo: string | null
  payslipsCount: number
  totalGross: number
  totalApitBase: number
  totalApit: number
}

interface T10Response {
  taxYear: string
  periodStart: string
  periodEnd: string
  employees: T10Row[]
  totals: { gross: number; apit: number }
}

// ────────────────────────────────────────────────────────────
// EPF C-Form (monthly contribution return)
// ────────────────────────────────────────────────────────────

interface EpfRow {
  employeeId: string
  memberNo: string | null
  nic: string | null
  fullName: string
  totalEarnings: number
  employeeContribution: number
  employerContribution: number
  totalContribution: number
}

interface EpfResponse {
  month: string
  employees: EpfRow[]
  totals: { earnings: number; employee: number; employer: number; total: number }
}

const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
]

function fmtMoney(n: number): string {
  return n.toLocaleString("en-LK", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

function suggestedTaxYear(): number {
  const now = new Date()
  return now.getMonth() < 3 ? now.getFullYear() - 1 : now.getFullYear()
}

function suggestedMonth(): { year: number; month: number } {
  // Default: previous calendar month — most common reporting cadence.
  const now = new Date()
  const prev = new Date(now.getFullYear(), now.getMonth() - 1, 1)
  return { year: prev.getFullYear(), month: prev.getMonth() + 1 }
}

// Generic tenant-API fetch + blob download for any statutory file.
async function downloadFile(slug: string, path: string, filename: string) {
  const token = localStorage.getItem("people.token") ?? ""
  const url = (import.meta.env.PROD ? "/people/api" : "/api") + `/v1/t/${slug}${path}`
  const res = await fetch(url, { headers: { authorization: `Bearer ${token}` } })
  if (!res.ok) throw new Error(`HTTP ${res.status}`)
  const blob = await res.blob()
  const link = document.createElement("a")
  link.href = URL.createObjectURL(blob)
  link.download = filename
  document.body.appendChild(link)
  link.click()
  link.remove()
}

export function ReportsPage() {
  const { slug = "" } = useParams<{ slug: string }>()
  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-2xl font-semibold">Statutory reports</h1>
        <p className="text-xs text-slate-500 mt-1">
          Tenant <span className="font-mono">{slug}</span>
        </p>
      </div>
      <T10Section slug={slug} />
      <EpfCFormSection slug={slug} />
      <EtfR4Section slug={slug} />
      <SalaryFileSection slug={slug} />
      <WhtSection slug={slug} />
      <div className="bg-white border border-slate-200 rounded p-4 text-sm text-slate-500">
        <div className="font-medium text-slate-700 mb-1">Phase 4 complete</div>
        All five SL statutory outputs ship: APIT T-10, EPF C-Form, ETF R4, salary disbursement file, WHT certificates.
      </div>
    </div>
  )
}

// ────────────────────────────────────────────────────────────
// T-10 section
// ────────────────────────────────────────────────────────────

function T10Section(props: { slug: string }) {
  const [year, setYear] = useState<number>(suggestedTaxYear())

  const t10 = useQuery({
    queryKey: ["report-t10", props.slug, year],
    enabled: !!props.slug,
    queryFn: () => apiTenant<T10Response>(props.slug, `/reports/apit-t10?year=${year}`),
  })

  const yearOptions: number[] = []
  for (let y = suggestedTaxYear() + 1; y >= suggestedTaxYear() - 5; y--) yearOptions.push(y)

  return (
    <div className="bg-white border border-slate-200 rounded">
      <div className="px-4 py-3 border-b border-slate-200 flex items-center gap-3">
        <div className="font-medium">APIT Form T-10 (annual)</div>
        <div className="ml-auto flex items-center gap-2">
          <label className="text-xs text-slate-500">Tax year</label>
          <select
            value={year}
            onChange={(e) => setYear(Number(e.target.value))}
            className="px-2 py-1 border border-slate-300 rounded text-sm bg-white"
          >
            {yearOptions.map((y) => (
              <option key={y} value={y}>{y}/{String(y + 1).slice(2)}</option>
            ))}
          </select>
          <button
            onClick={() =>
              downloadFile(
                props.slug,
                `/reports/apit-t10?year=${year}&format=pdf`,
                `apit-t10-${year}-${String(year + 1).slice(2)}.pdf`,
              ).catch((e) => alert(`Download failed: ${e.message}`))
            }
            disabled={!t10.data || t10.data.employees.length === 0}
            className="px-3 py-1.5 bg-brand-600 hover:bg-brand-700 disabled:bg-slate-300 text-white text-xs font-medium rounded"
          >
            Download PDF
          </button>
        </div>
      </div>

      {t10.isLoading && <div className="p-6 text-sm text-slate-400">Loading…</div>}
      {t10.error && <div className="p-6 text-sm text-red-600">{(t10.error as Error).message}</div>}

      {t10.data && t10.data.employees.length === 0 && (
        <div className="p-8 text-center text-sm text-slate-400">
          No approved or paid runs in tax year {t10.data.taxYear}. Approve at least one payroll run before generating T-10.
        </div>
      )}

      {t10.data && t10.data.employees.length > 0 && (
        <>
          <div className="px-4 py-2 bg-slate-50 border-b border-slate-200 text-xs text-slate-500 flex justify-between">
            <span>{t10.data.taxYear} · {t10.data.employees.length} employee{t10.data.employees.length === 1 ? "" : "s"}</span>
            <span>Total APIT: <strong className="font-mono text-slate-900">LKR {fmtMoney(t10.data.totals.apit)}</strong></span>
          </div>
          <table className="w-full text-sm">
            <thead className="text-xs text-slate-500 uppercase tracking-wide border-b border-slate-200">
              <tr>
                <th className="text-left px-4 py-2 font-medium">Emp #</th>
                <th className="text-left px-4 py-2 font-medium">NIC</th>
                <th className="text-left px-4 py-2 font-medium">Name</th>
                <th className="text-left px-4 py-2 font-medium">EPF #</th>
                <th className="text-right px-4 py-2 font-medium">Slips</th>
                <th className="text-right px-4 py-2 font-medium">Earnings</th>
                <th className="text-right px-4 py-2 font-medium">APIT base</th>
                <th className="text-right px-4 py-2 font-medium">APIT</th>
              </tr>
            </thead>
            <tbody>
              {t10.data.employees.map((r) => (
                <tr key={r.employeeId} className="border-b border-slate-100 last:border-b-0 hover:bg-slate-50">
                  <td className="px-4 py-1.5 font-mono text-xs">{r.employeeNo}</td>
                  <td className="px-4 py-1.5 font-mono text-xs">{r.nic ?? "—"}</td>
                  <td className="px-4 py-1.5">{r.fullName}</td>
                  <td className="px-4 py-1.5 font-mono text-xs">{r.epfNo ?? "—"}</td>
                  <td className="px-4 py-1.5 text-right font-mono">{r.payslipsCount}</td>
                  <td className="px-4 py-1.5 text-right font-mono">{fmtMoney(r.totalGross)}</td>
                  <td className="px-4 py-1.5 text-right font-mono">{fmtMoney(r.totalApitBase)}</td>
                  <td className="px-4 py-1.5 text-right font-mono font-semibold">{fmtMoney(r.totalApit)}</td>
                </tr>
              ))}
              <tr className="border-t-2 border-slate-300 bg-slate-50">
                <td className="px-4 py-2 font-semibold" colSpan={5}>Totals</td>
                <td className="px-4 py-2 text-right font-mono font-semibold">{fmtMoney(t10.data.totals.gross)}</td>
                <td className="px-4 py-2 text-right"></td>
                <td className="px-4 py-2 text-right font-mono font-semibold">{fmtMoney(t10.data.totals.apit)}</td>
              </tr>
            </tbody>
          </table>
        </>
      )}
    </div>
  )
}

// ────────────────────────────────────────────────────────────
// EPF C-Form section
// ────────────────────────────────────────────────────────────

function EpfCFormSection(props: { slug: string }) {
  const init = suggestedMonth()
  const [year, setYear] = useState<number>(init.year)
  const [month, setMonth] = useState<number>(init.month)

  const epf = useQuery({
    queryKey: ["report-epf-c", props.slug, year, month],
    enabled: !!props.slug,
    queryFn: () => apiTenant<EpfResponse>(props.slug, `/reports/epf-c-form?year=${year}&month=${month}`),
  })

  const yearOptions: number[] = []
  const baseY = suggestedMonth().year
  for (let y = baseY + 1; y >= baseY - 5; y--) yearOptions.push(y)

  const has = !!epf.data && epf.data.employees.length > 0

  return (
    <div className="bg-white border border-slate-200 rounded">
      <div className="px-4 py-3 border-b border-slate-200 flex items-center gap-3 flex-wrap">
        <div className="font-medium">EPF Form C (monthly contributions)</div>
        <div className="ml-auto flex items-center gap-2 flex-wrap">
          <label className="text-xs text-slate-500">Month</label>
          <select
            value={month}
            onChange={(e) => setMonth(Number(e.target.value))}
            className="px-2 py-1 border border-slate-300 rounded text-sm bg-white"
          >
            {MONTHS.map((m, i) => (
              <option key={m} value={i + 1}>{m}</option>
            ))}
          </select>
          <select
            value={year}
            onChange={(e) => setYear(Number(e.target.value))}
            className="px-2 py-1 border border-slate-300 rounded text-sm bg-white"
          >
            {yearOptions.map((y) => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>
          <button
            onClick={() =>
              downloadFile(
                props.slug,
                `/reports/epf-c-form?year=${year}&month=${month}&format=csv`,
                `epf-c-form-${year}-${String(month).padStart(2, "0")}.csv`,
              ).catch((e) => alert(`Download failed: ${e.message}`))
            }
            disabled={!has}
            className="px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 disabled:bg-slate-300 text-white text-xs font-medium rounded"
          >
            Download CSV
          </button>
          <button
            onClick={() =>
              downloadFile(
                props.slug,
                `/reports/epf-c-form?year=${year}&month=${month}&format=pdf`,
                `epf-c-form-${year}-${String(month).padStart(2, "0")}.pdf`,
              ).catch((e) => alert(`Download failed: ${e.message}`))
            }
            disabled={!has}
            className="px-3 py-1.5 bg-brand-600 hover:bg-brand-700 disabled:bg-slate-300 text-white text-xs font-medium rounded"
          >
            Download PDF
          </button>
        </div>
      </div>

      {epf.isLoading && <div className="p-6 text-sm text-slate-400">Loading…</div>}
      {epf.error && <div className="p-6 text-sm text-red-600">{(epf.error as Error).message}</div>}

      {epf.data && epf.data.employees.length === 0 && (
        <div className="p-8 text-center text-sm text-slate-400">
          No approved or paid runs for {MONTHS[month - 1]} {year}. Approve a run before generating C-Form.
        </div>
      )}

      {has && (
        <>
          <div className="px-4 py-2 bg-slate-50 border-b border-slate-200 text-xs text-slate-500 flex justify-between">
            <span>
              {MONTHS[month - 1]} {year} · {epf.data!.employees.length} member{epf.data!.employees.length === 1 ? "" : "s"}
            </span>
            <span>
              Total contribution (20%): <strong className="font-mono text-slate-900">LKR {fmtMoney(epf.data!.totals.total)}</strong>
            </span>
          </div>
          <table className="w-full text-sm">
            <thead className="text-xs text-slate-500 uppercase tracking-wide border-b border-slate-200">
              <tr>
                <th className="text-left px-4 py-2 font-medium">Member #</th>
                <th className="text-left px-4 py-2 font-medium">NIC</th>
                <th className="text-left px-4 py-2 font-medium">Name</th>
                <th className="text-right px-4 py-2 font-medium">Earnings</th>
                <th className="text-right px-4 py-2 font-medium">Employee 8%</th>
                <th className="text-right px-4 py-2 font-medium">Employer 12%</th>
                <th className="text-right px-4 py-2 font-medium">Total</th>
              </tr>
            </thead>
            <tbody>
              {epf.data!.employees.map((r) => (
                <tr key={r.employeeId} className="border-b border-slate-100 last:border-b-0 hover:bg-slate-50">
                  <td className="px-4 py-1.5 font-mono text-xs">{r.memberNo ?? "—"}</td>
                  <td className="px-4 py-1.5 font-mono text-xs">{r.nic ?? "—"}</td>
                  <td className="px-4 py-1.5">{r.fullName}</td>
                  <td className="px-4 py-1.5 text-right font-mono">{fmtMoney(r.totalEarnings)}</td>
                  <td className="px-4 py-1.5 text-right font-mono">{fmtMoney(r.employeeContribution)}</td>
                  <td className="px-4 py-1.5 text-right font-mono">{fmtMoney(r.employerContribution)}</td>
                  <td className="px-4 py-1.5 text-right font-mono font-semibold">{fmtMoney(r.totalContribution)}</td>
                </tr>
              ))}
              <tr className="border-t-2 border-slate-300 bg-slate-50">
                <td className="px-4 py-2 font-semibold" colSpan={3}>Totals</td>
                <td className="px-4 py-2 text-right font-mono font-semibold">{fmtMoney(epf.data!.totals.earnings)}</td>
                <td className="px-4 py-2 text-right font-mono font-semibold">{fmtMoney(epf.data!.totals.employee)}</td>
                <td className="px-4 py-2 text-right font-mono font-semibold">{fmtMoney(epf.data!.totals.employer)}</td>
                <td className="px-4 py-2 text-right font-mono font-semibold">{fmtMoney(epf.data!.totals.total)}</td>
              </tr>
            </tbody>
          </table>
        </>
      )}
    </div>
  )
}

// ────────────────────────────────────────────────────────────
// ETF R4 (monthly contribution schedule)
// ────────────────────────────────────────────────────────────

interface EtfRow {
  employeeId: string
  memberNo: string | null
  nic: string | null
  fullName: string
  totalEarnings: number
  etfContribution: number
}

interface EtfResponse {
  month: string
  employees: EtfRow[]
  totals: { earnings: number; etf: number }
}

function EtfR4Section(props: { slug: string }) {
  const init = suggestedMonth()
  const [year, setYear] = useState<number>(init.year)
  const [month, setMonth] = useState<number>(init.month)

  const etf = useQuery({
    queryKey: ["report-etf-r4", props.slug, year, month],
    enabled: !!props.slug,
    queryFn: () => apiTenant<EtfResponse>(props.slug, `/reports/etf-r4?year=${year}&month=${month}`),
  })

  const yearOptions: number[] = []
  const baseY = suggestedMonth().year
  for (let y = baseY + 1; y >= baseY - 5; y--) yearOptions.push(y)

  const has = !!etf.data && etf.data.employees.length > 0

  return (
    <div className="bg-white border border-slate-200 rounded">
      <div className="px-4 py-3 border-b border-slate-200 flex items-center gap-3 flex-wrap">
        <div className="font-medium">ETF R4 (monthly contributions)</div>
        <div className="ml-auto flex items-center gap-2 flex-wrap">
          <label className="text-xs text-slate-500">Month</label>
          <select
            value={month}
            onChange={(e) => setMonth(Number(e.target.value))}
            className="px-2 py-1 border border-slate-300 rounded text-sm bg-white"
          >
            {MONTHS.map((m, i) => (
              <option key={m} value={i + 1}>{m}</option>
            ))}
          </select>
          <select
            value={year}
            onChange={(e) => setYear(Number(e.target.value))}
            className="px-2 py-1 border border-slate-300 rounded text-sm bg-white"
          >
            {yearOptions.map((y) => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>
          <button
            onClick={() =>
              downloadFile(
                props.slug,
                `/reports/etf-r4?year=${year}&month=${month}&format=csv`,
                `etf-r4-${year}-${String(month).padStart(2, "0")}.csv`,
              ).catch((e) => alert(`Download failed: ${e.message}`))
            }
            disabled={!has}
            className="px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 disabled:bg-slate-300 text-white text-xs font-medium rounded"
          >
            Download CSV
          </button>
          <button
            onClick={() =>
              downloadFile(
                props.slug,
                `/reports/etf-r4?year=${year}&month=${month}&format=pdf`,
                `etf-r4-${year}-${String(month).padStart(2, "0")}.pdf`,
              ).catch((e) => alert(`Download failed: ${e.message}`))
            }
            disabled={!has}
            className="px-3 py-1.5 bg-brand-600 hover:bg-brand-700 disabled:bg-slate-300 text-white text-xs font-medium rounded"
          >
            Download PDF
          </button>
        </div>
      </div>

      {etf.isLoading && <div className="p-6 text-sm text-slate-400">Loading…</div>}
      {etf.error && <div className="p-6 text-sm text-red-600">{(etf.error as Error).message}</div>}

      {etf.data && etf.data.employees.length === 0 && (
        <div className="p-8 text-center text-sm text-slate-400">
          No approved or paid runs for {MONTHS[month - 1]} {year}. Approve a run before generating R4.
        </div>
      )}

      {has && (
        <>
          <div className="px-4 py-2 bg-slate-50 border-b border-slate-200 text-xs text-slate-500 flex justify-between">
            <span>
              {MONTHS[month - 1]} {year} · {etf.data!.employees.length} member{etf.data!.employees.length === 1 ? "" : "s"}
            </span>
            <span>
              Total ETF (3%): <strong className="font-mono text-slate-900">LKR {fmtMoney(etf.data!.totals.etf)}</strong>
            </span>
          </div>
          <table className="w-full text-sm">
            <thead className="text-xs text-slate-500 uppercase tracking-wide border-b border-slate-200">
              <tr>
                <th className="text-left px-4 py-2 font-medium">Member #</th>
                <th className="text-left px-4 py-2 font-medium">NIC</th>
                <th className="text-left px-4 py-2 font-medium">Name</th>
                <th className="text-right px-4 py-2 font-medium">Earnings</th>
                <th className="text-right px-4 py-2 font-medium">ETF 3%</th>
              </tr>
            </thead>
            <tbody>
              {etf.data!.employees.map((r) => (
                <tr key={r.employeeId} className="border-b border-slate-100 last:border-b-0 hover:bg-slate-50">
                  <td className="px-4 py-1.5 font-mono text-xs">{r.memberNo ?? "—"}</td>
                  <td className="px-4 py-1.5 font-mono text-xs">{r.nic ?? "—"}</td>
                  <td className="px-4 py-1.5">{r.fullName}</td>
                  <td className="px-4 py-1.5 text-right font-mono">{fmtMoney(r.totalEarnings)}</td>
                  <td className="px-4 py-1.5 text-right font-mono font-semibold">{fmtMoney(r.etfContribution)}</td>
                </tr>
              ))}
              <tr className="border-t-2 border-slate-300 bg-slate-50">
                <td className="px-4 py-2 font-semibold" colSpan={3}>Totals</td>
                <td className="px-4 py-2 text-right font-mono font-semibold">{fmtMoney(etf.data!.totals.earnings)}</td>
                <td className="px-4 py-2 text-right font-mono font-semibold">{fmtMoney(etf.data!.totals.etf)}</td>
              </tr>
            </tbody>
          </table>
        </>
      )}
    </div>
  )
}

// ────────────────────────────────────────────────────────────
// Salary disbursement file (monthly bank upload)
// ────────────────────────────────────────────────────────────

interface SalaryFileRow {
  employeeId: string
  employeeNo: string
  fullName: string
  bankAccountName: string | null
  bankAccountNumber: string | null
  bankBranchCode: string | null
  bankName: string | null
  netPay: number
  reference: string
}

interface SalaryFileResponse {
  month: string
  payable: SalaryFileRow[]
  skipped: SalaryFileRow[]
  totals: { count: number; amount: number }
}

function SalaryFileSection(props: { slug: string }) {
  const init = suggestedMonth()
  const [year, setYear] = useState<number>(init.year)
  const [month, setMonth] = useState<number>(init.month)

  const sf = useQuery({
    queryKey: ["report-salary-file", props.slug, year, month],
    enabled: !!props.slug,
    queryFn: () => apiTenant<SalaryFileResponse>(props.slug, `/reports/salary-file?year=${year}&month=${month}`),
  })

  const yearOptions: number[] = []
  const baseY = suggestedMonth().year
  for (let y = baseY + 1; y >= baseY - 5; y--) yearOptions.push(y)

  const has = !!sf.data && (sf.data.payable.length > 0 || sf.data.skipped.length > 0)
  const payableCount = sf.data?.payable.length ?? 0

  return (
    <div className="bg-white border border-slate-200 rounded">
      <div className="px-4 py-3 border-b border-slate-200 flex items-center gap-3 flex-wrap">
        <div className="font-medium">Salary disbursement file (bank upload)</div>
        <div className="ml-auto flex items-center gap-2 flex-wrap">
          <label className="text-xs text-slate-500">Month</label>
          <select
            value={month}
            onChange={(e) => setMonth(Number(e.target.value))}
            className="px-2 py-1 border border-slate-300 rounded text-sm bg-white"
          >
            {MONTHS.map((m, i) => (
              <option key={m} value={i + 1}>{m}</option>
            ))}
          </select>
          <select
            value={year}
            onChange={(e) => setYear(Number(e.target.value))}
            className="px-2 py-1 border border-slate-300 rounded text-sm bg-white"
          >
            {yearOptions.map((y) => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>
          <button
            onClick={() =>
              downloadFile(
                props.slug,
                `/reports/salary-file?year=${year}&month=${month}&format=csv`,
                `salary-file-${year}-${String(month).padStart(2, "0")}.csv`,
              ).catch((e) => alert(`Download failed: ${e.message}`))
            }
            disabled={payableCount === 0}
            className="px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 disabled:bg-slate-300 text-white text-xs font-medium rounded"
          >
            Download CSV
          </button>
        </div>
      </div>

      {sf.isLoading && <div className="p-6 text-sm text-slate-400">Loading…</div>}
      {sf.error && <div className="p-6 text-sm text-red-600">{(sf.error as Error).message}</div>}

      {sf.data && !has && (
        <div className="p-8 text-center text-sm text-slate-400">
          No approved or paid runs for {MONTHS[month - 1]} {year}.
        </div>
      )}

      {sf.data && has && (
        <>
          <div className="px-4 py-2 bg-slate-50 border-b border-slate-200 text-xs text-slate-500 flex justify-between">
            <span>
              {MONTHS[month - 1]} {year} · {payableCount} payable
              {sf.data.skipped.length > 0 && (
                <>, <span className="text-amber-700">{sf.data.skipped.length} skipped (no bank info)</span></>
              )}
            </span>
            <span>
              Total disbursement: <strong className="font-mono text-slate-900">LKR {fmtMoney(sf.data.totals.amount)}</strong>
            </span>
          </div>

          {payableCount > 0 && (
            <table className="w-full text-sm">
              <thead className="text-xs text-slate-500 uppercase tracking-wide border-b border-slate-200">
                <tr>
                  <th className="text-left px-4 py-2 font-medium">Emp #</th>
                  <th className="text-left px-4 py-2 font-medium">Account name</th>
                  <th className="text-left px-4 py-2 font-medium">Account #</th>
                  <th className="text-left px-4 py-2 font-medium">Branch</th>
                  <th className="text-left px-4 py-2 font-medium">Bank</th>
                  <th className="text-right px-4 py-2 font-medium">Amount</th>
                </tr>
              </thead>
              <tbody>
                {sf.data.payable.map((r) => (
                  <tr key={r.employeeId} className="border-b border-slate-100 last:border-b-0 hover:bg-slate-50">
                    <td className="px-4 py-1.5 font-mono text-xs">{r.employeeNo}</td>
                    <td className="px-4 py-1.5">{r.bankAccountName ?? r.fullName}</td>
                    <td className="px-4 py-1.5 font-mono text-xs">{r.bankAccountNumber}</td>
                    <td className="px-4 py-1.5 font-mono text-xs">{r.bankBranchCode ?? "—"}</td>
                    <td className="px-4 py-1.5 text-xs">{r.bankName ?? "—"}</td>
                    <td className="px-4 py-1.5 text-right font-mono font-semibold">{fmtMoney(r.netPay)}</td>
                  </tr>
                ))}
                <tr className="border-t-2 border-slate-300 bg-slate-50">
                  <td className="px-4 py-2 font-semibold" colSpan={5}>
                    Total ({payableCount} payment{payableCount === 1 ? "" : "s"})
                  </td>
                  <td className="px-4 py-2 text-right font-mono font-semibold">{fmtMoney(sf.data.totals.amount)}</td>
                </tr>
              </tbody>
            </table>
          )}

          {sf.data.skipped.length > 0 && (
            <div className="border-t border-slate-200 bg-amber-50">
              <div className="px-4 py-2 text-xs text-amber-800 font-medium">
                Skipped — bank details missing
              </div>
              <table className="w-full text-sm">
                <tbody>
                  {sf.data.skipped.map((r) => (
                    <tr key={r.employeeId} className="border-b border-amber-100 last:border-b-0">
                      <td className="px-4 py-1.5 font-mono text-xs w-24">{r.employeeNo}</td>
                      <td className="px-4 py-1.5">{r.fullName}</td>
                      <td className="px-4 py-1.5 text-right font-mono text-xs text-slate-500">
                        Net LKR {fmtMoney(r.netPay)} not paid
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="px-4 py-2 text-[11px] text-amber-700">
                Add bank account # / branch code via Employees → edit, then regenerate.
              </div>
            </div>
          )}
        </>
      )}
    </div>
  )
}

// ────────────────────────────────────────────────────────────
// WHT certificates (ISP withholding under IRA s.84/85)
// ────────────────────────────────────────────────────────────

interface WhtCertRow {
  payslipId: string
  certNumber: string
  employeeNo: string
  fullName: string
  nic: string | null
  tin: string | null
  grossAmount: number
  whtAmount: number
}

interface WhtSummaryResponse {
  month: string
  certificates: WhtCertRow[]
  totals: { count: number; gross: number; wht: number }
}

function WhtSection(props: { slug: string }) {
  const init = suggestedMonth()
  const [year, setYear] = useState<number>(init.year)
  const [month, setMonth] = useState<number>(init.month)

  const wht = useQuery({
    queryKey: ["report-wht", props.slug, year, month],
    enabled: !!props.slug,
    queryFn: () => apiTenant<WhtSummaryResponse>(props.slug, `/reports/wht-summary?year=${year}&month=${month}`),
  })

  const yearOptions: number[] = []
  const baseY = suggestedMonth().year
  for (let y = baseY + 1; y >= baseY - 5; y--) yearOptions.push(y)

  const has = !!wht.data && wht.data.certificates.length > 0

  function downloadCert(payslipId: string, certNumber: string) {
    downloadFile(
      props.slug,
      `/reports/wht/certificate/${payslipId}?format=pdf`,
      `${certNumber}.pdf`,
    ).catch((e) => alert(`Download failed: ${e.message}`))
  }

  return (
    <div className="bg-white border border-slate-200 rounded">
      <div className="px-4 py-3 border-b border-slate-200 flex items-center gap-3 flex-wrap">
        <div>
          <div className="font-medium">WHT certificates (ISP — IRA s.84/85)</div>
          <div className="text-xs text-slate-500 mt-0.5">5% withholding on monthly ISP payments above LKR 100,000</div>
        </div>
        <div className="ml-auto flex items-center gap-2 flex-wrap">
          <label className="text-xs text-slate-500">Month</label>
          <select
            value={month}
            onChange={(e) => setMonth(Number(e.target.value))}
            className="px-2 py-1 border border-slate-300 rounded text-sm bg-white"
          >
            {MONTHS.map((m, i) => (
              <option key={m} value={i + 1}>{m}</option>
            ))}
          </select>
          <select
            value={year}
            onChange={(e) => setYear(Number(e.target.value))}
            className="px-2 py-1 border border-slate-300 rounded text-sm bg-white"
          >
            {yearOptions.map((y) => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>
          <button
            onClick={() =>
              downloadFile(
                props.slug,
                `/reports/wht-summary?year=${year}&month=${month}&format=csv`,
                `wht-summary-${year}-${String(month).padStart(2, "0")}.csv`,
              ).catch((e) => alert(`Download failed: ${e.message}`))
            }
            disabled={!has}
            className="px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 disabled:bg-slate-300 text-white text-xs font-medium rounded"
          >
            Summary CSV
          </button>
          <button
            onClick={() =>
              downloadFile(
                props.slug,
                `/reports/wht-summary?year=${year}&month=${month}&format=pdf`,
                `wht-summary-${year}-${String(month).padStart(2, "0")}.pdf`,
              ).catch((e) => alert(`Download failed: ${e.message}`))
            }
            disabled={!has}
            className="px-3 py-1.5 bg-brand-600 hover:bg-brand-700 disabled:bg-slate-300 text-white text-xs font-medium rounded"
          >
            Summary PDF
          </button>
        </div>
      </div>

      {wht.isLoading && <div className="p-6 text-sm text-slate-400">Loading…</div>}
      {wht.error && <div className="p-6 text-sm text-red-600">{(wht.error as Error).message}</div>}

      {wht.data && wht.data.certificates.length === 0 && (
        <div className="p-8 text-center text-sm text-slate-400">
          No ISP payments triggered WHT in {MONTHS[month - 1]} {year}. (ISP contractors only — no certificates for regular employees.)
        </div>
      )}

      {has && (
        <>
          <div className="px-4 py-2 bg-slate-50 border-b border-slate-200 text-xs text-slate-500 flex justify-between">
            <span>
              {MONTHS[month - 1]} {year} · {wht.data!.certificates.length} certificate{wht.data!.certificates.length === 1 ? "" : "s"}
            </span>
            <span>
              Total WHT remittable: <strong className="font-mono text-slate-900">LKR {fmtMoney(wht.data!.totals.wht)}</strong>
            </span>
          </div>
          <table className="w-full text-sm">
            <thead className="text-xs text-slate-500 uppercase tracking-wide border-b border-slate-200">
              <tr>
                <th className="text-left px-4 py-2 font-medium">Certificate #</th>
                <th className="text-left px-4 py-2 font-medium">Recipient</th>
                <th className="text-left px-4 py-2 font-medium">NIC / TIN</th>
                <th className="text-right px-4 py-2 font-medium">Gross</th>
                <th className="text-right px-4 py-2 font-medium">WHT 5%</th>
                <th className="text-right px-4 py-2 font-medium"></th>
              </tr>
            </thead>
            <tbody>
              {wht.data!.certificates.map((c) => (
                <tr key={c.payslipId} className="border-b border-slate-100 last:border-b-0 hover:bg-slate-50">
                  <td className="px-4 py-1.5 font-mono text-xs">{c.certNumber}</td>
                  <td className="px-4 py-1.5">
                    <div>{c.fullName}</div>
                    <div className="text-xs text-slate-400 font-mono">{c.employeeNo}</div>
                  </td>
                  <td className="px-4 py-1.5 text-xs text-slate-600">
                    <div className="font-mono">{c.nic ?? "—"}</div>
                    <div className="text-slate-400 font-mono">{c.tin ?? "no TIN"}</div>
                  </td>
                  <td className="px-4 py-1.5 text-right font-mono">{fmtMoney(c.grossAmount)}</td>
                  <td className="px-4 py-1.5 text-right font-mono font-semibold">{fmtMoney(c.whtAmount)}</td>
                  <td className="px-4 py-1.5 text-right">
                    <button
                      onClick={() => downloadCert(c.payslipId, c.certNumber)}
                      className="px-2 py-1 bg-brand-600 hover:bg-brand-700 text-white text-xs font-medium rounded"
                    >
                      Certificate PDF
                    </button>
                  </td>
                </tr>
              ))}
              <tr className="border-t-2 border-slate-300 bg-slate-50">
                <td className="px-4 py-2 font-semibold" colSpan={3}>
                  Totals ({wht.data!.totals.count} certificate{wht.data!.totals.count === 1 ? "" : "s"})
                </td>
                <td className="px-4 py-2 text-right font-mono font-semibold">{fmtMoney(wht.data!.totals.gross)}</td>
                <td className="px-4 py-2 text-right font-mono font-semibold">{fmtMoney(wht.data!.totals.wht)}</td>
                <td></td>
              </tr>
            </tbody>
          </table>
        </>
      )}
    </div>
  )
}
